import { useCallback, useMemo, useState, MouseEventHandler, ComponentProps } from 'react';
import { TenantTable } from './components/TenantTable/TenantTable';
import { TENANT_TABLE_VIEW_ARRAY } from './constants';
import { Box, Button } from '@mui/material';
import TenantsSearchFilters, {
  TenantsSearchSubmitPayload,
} from './components/TenantsSearchFilters';
import { useTenantsTableBulkSelectMenu } from './components/TenantTable/helpers/useTenantsTableBulkSelectMenu';
import { useTenantsTableData } from './components/TenantTable/helpers/useTenantsTableData';
import { useTenants } from './hooks/useTenants';
import { FetchTenantPayload } from './types';
import { ToggleOptionsButton } from 'components/ToggleOptionsButton';
import StandardPageHeader from 'components/StandardPageHeader';
import { MinimalCard } from 'components/styled';
import useExportToExcel from 'hooks/useExportToExcel';
import { Download } from 'lucide-react';

export const TenantsContainer = () => {
  const [submittedSearchText, setSubmittedSearchText] = useState('');
  const [submittedFilterParams, setSubmittedFilterParams] = useState<FetchTenantPayload | null>(
    null,
  );

  const { setTenantTableView, headerLabel, tenantTableView, isLoading, tenantIds } =
    useTenantsTableData({ filterParams: submittedFilterParams });
  const {
    openTenantTableBulkSelectMenu,
    setSelectedTenantIdsSet,
    selectedTenantIds,
    selectTenant,
  } = useTenantsTableBulkSelectMenu();
  const { tenantsHash } = useTenants();

  const onFiltersSubmit = useCallback(
    ({ searchText, filterParams }: TenantsSearchSubmitPayload) => {
      setSubmittedSearchText(searchText);
      // undefined = text-only submit (e.g. URL-driven), keep current filters
      if (filterParams !== undefined) {
        setSubmittedFilterParams(filterParams);
      }
      setSelectedTenantIdsSet(new Set());
    },
    [setSelectedTenantIdsSet],
  );

  // Text search matches tenant names client-side (case-insensitive) — the
  // backend's tenantName LIKE filter is case-sensitive, and the full tenant
  // list is already loaded
  const filteredTenantIds = useMemo(() => {
    const normalizedSearchText = submittedSearchText.trim().toLowerCase();
    if (!normalizedSearchText) return tenantIds;
    return tenantIds.filter((tenantId) =>
      tenantsHash[tenantId]?.tenantName?.toLowerCase().includes(normalizedSearchText),
    );
  }, [tenantIds, tenantsHash, submittedSearchText]);

  // Export the filtered result list (Teams pattern: save/download search results)
  const { exportToExcel } = useExportToExcel();
  const onExport = useCallback(() => {
    const rows = filteredTenantIds.flatMap((tenantId) => {
      const tenant = tenantsHash[tenantId];
      if (!tenant) return [];
      return {
        ID: tenant.id,
        'Tenant Name': tenant.tenantName,
        Status: tenant.status,
        'Tenant Type': tenant.tenantType ?? '',
        Creator: tenant.creator ?? '',
        'Created At': tenant.createdAt ?? '',
        Tags: (tenant.tags || []).join(', '),
        CSM: tenant.csm ?? '',
        'Archived At': tenant.archivedAt ?? '',
        'Archived By': tenant.archiver ?? '',
      };
    });
    exportToExcel({ data: rows, fileName: 'tenants' });
  }, [exportToExcel, filteredTenantIds, tenantsHash]);

  const numSelected = selectedTenantIds.size;
  const disableBulkMenu = !numSelected;

  const onViewToggle: ComponentProps<typeof ToggleOptionsButton>['onSelect'] = useCallback(
    ({
      option,
    }: Parameters<NonNullable<ComponentProps<typeof ToggleOptionsButton>['onSelect']>>[0]) => {
      setTenantTableView(option.value);
    },
    [setTenantTableView],
  );

  const TenantTableViewToggle = (
    <ToggleOptionsButton
      options={TENANT_TABLE_VIEW_ARRAY}
      currentOption={tenantTableView}
      onSelect={onViewToggle}
      isDisabled={isLoading}
    />
  );

  const onModifyClick: MouseEventHandler<HTMLElement> = (e) => {
    openTenantTableBulkSelectMenu({ target: e.currentTarget });
  };

  return (
    <Box
      component={'div'}
      sx={{
        backgroundColor: 'transparent',
        padding: 4,
      }}
    >
      <StandardPageHeader
        title={headerLabel}
        subtitle="Manage tenant configurations and settings"
        icon="Tenant"
        sx={{ mb: 6 }}
      />

      <TenantsSearchFilters onSubmit={onFiltersSubmit} isLoading={isLoading} />

      <MinimalCard $hoverEffect={false} sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          {TenantTableViewToggle}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Download size={16} />}
              disabled={!filteredTenantIds.length}
              onClick={onExport}
            >
              Export
            </Button>
            <Button
              variant="outlined"
              color="primary"
              disabled={disableBulkMenu}
              onClick={onModifyClick}
            >
              Modify
              <Box
                component="span"
                sx={{ display: 'inline-block', minWidth: 20, textAlign: 'right', ml: 0.5 }}
              >
                {numSelected > 0 ? numSelected : ''}
              </Box>
            </Button>
          </Box>
        </Box>
      </MinimalCard>

      <MinimalCard $hoverEffect={false}>
        <TenantTable
          variant={tenantTableView}
          isLoading={isLoading}
          selectTenant={selectTenant}
          selectedTenantIds={selectedTenantIds}
          tenantIds={filteredTenantIds}
        />
      </MinimalCard>
    </Box>
  );
};
